import React = require('react');
import { useEffect, useState } from 'react';
import { concat, flow, get } from 'lodash';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { IAuthState } from '../../../../../../services/auth/models';
import { destroy, reset } from 'redux-form';
import { IMtaActions, IMtaState } from '../../../../../../services/mta/models';
import { mtaActions } from '../../../../../../services/mta/actions';
import Loading from '../../../../../../components/Loading';
import { withJob } from 'react-jobs';
import { actions as brandingActions } from '../../../../../../services/branding/reducer';
import { IBrandingActions, IBrandingState } from '../../../../../../services/branding/models';
import { ICustomerMTAOptions } from '../../index';
import { paymentMethodActions } from '../../../../../../services/paymentMethod/actions';
import { IPaymentMethodActions } from '../../../../../../services/paymentMethod/models';
import { actions as quoteActions } from '../../../../../../services/quote/reducer';
import { IQuoteActions } from '../../../../../../services/quote/models';
import { InfoAnnie } from '../../../../../../components/InfoAnnie';
import { MTAButtons } from '../MTAButtons';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import {
  alreadyAddedOptions,
  canCancel,
  checkIfAllOptionsAreAdded,
  findPolicyRenewal,
  handleClick,
  resetForms,
  setSelectedPolicy
} from './MTAHelpers';
import { MTARemoveOption } from './Components/MTARemoveOptions';
import { MTAAddOptions } from './Components/MTAAddoptions';
import { MTAChangeDetails } from './Components/MTAChangeDetails';
import { parse } from 'date-fns';
import * as api from '../../../../../../data/api/api';
import { IPolicyClaims } from '../../../../../../data/api/models';

export const enum MtaSelection {
  PERSONAL_DETAILS = 'personal-details',
  COVER_DETAILS = 'cover-details',
  AMT_UPSELL = 'Upgrade to Annual',
  CANCEL_POLICY = 'cancel-policy',
  CANCEL_RENEWAL = 'cancel-renewal',
  ADDITIONAL_OPTION = 'Additional Option',
  REMOVE_OPTION = 'Remove Option',
  ADD_TRAVELLER = 'Add Traveller',
  ADD_MEDICAL = 'Add Medical',
  REMOVE_TRAVELLER = 'Remove Traveller',
  CHANGE_REGION = 'Change Region',
  CHANGE_TRIP_DURATION = 'Change Maximum Trip Duration',
  CHANGE_TIER = 'Change Tier',
  CHANGE_DESTINATION = 'Change Destination',
  CHANGE_TRIP_DATE = 'Change Trip Date',
}

export const ALLOWED_CONTROL_GROUPS = [0, 1, 3, 5, 6, 9];

interface IMakeAChangeProps {
  auth: IAuthState;
  branding: IBrandingState;
  mta: IMtaState;
  customerMTAOptions: ICustomerMTAOptions;
  match: any;
  actions: {
    push(path: string);
    reset(formName: string);
    destroy(formName: string);
    quote: IQuoteActions;
    mta: IMtaActions;
    branding: IBrandingActions;
    paymentMethod: IPaymentMethodActions;
  };
}

const MakeAChangeSelection = ({ auth, match, mta, actions, customerMTAOptions }: IMakeAChangeProps) => {
  const policyId = match.params.id;
  const [allowedMta, setAllowedMta] = useState(true);
  const [disabledMtaSelections, setDisabledMtaSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTravellerSelection, setShowTravellerSelection] = useState(false);
  const [showTripDetailsSelection, setShowTripDetailsSelection] = useState(false);
  const [showTravellerDetailsSection, setShowTravellerDetailsSection] = useState(true);
  const disableUpdateMedical = disabledMtaSelections.includes(MtaSelection.ADD_MEDICAL)

  const handleSetLoading = () => {
    setLoading(false);
  };

  const showTravellerOptions = () => {
    setShowTravellerSelection(!showTravellerSelection)
  }
  const showTripDetailsOptions = () => {
    setShowTripDetailsSelection(!showTripDetailsSelection)
  }

  useEffect(() => {
    setLoading(true);
    resetForms(actions);
    const promises = [];
    const track = true;

    promises.push(api.getCustomerRateableClaims())
    promises.push(actions.mta.setMTACancellationOptions(get(customerMTAOptions, 'cancellation')));
    promises.push(actions.mta.setUpsellRules(false));

    Promise.all(promises).then((results) => {
      // Retrieve the claims from getCustomerRateableClaims.
      const claims = results[0]
      // Find any claims related to this policy
      const policyClaims = claims.filter((claim: IPolicyClaims) => claim?.attributes?.policy_id === match.params.id)
      // Loop through the claims on this policy, and restrict MTA access accordingly.
      policyClaims.forEach((claim: IPolicyClaims) => {
        // Open claims will prevent all MTA.
        if (get(claim, 'id', null) !== null && get(claim, 'attributes.claim_status') === 'open') {
          setAllowedMta(false)
          setLoading(false);
          // If any are open, we can exit here with MTA disabled
          return;
        }

        // Medical or Cancellation claims may not have travellers or medical updated, even when closed.
        const limitedMtaClaimTypes = ['medical', 'cancellation'];
        if (limitedMtaClaimTypes.includes(claim?.attributes?.claim_type)) {
          setDisabledMtaSelections((prevDisabledSelections) => [
            ...prevDisabledSelections,
            MtaSelection.ADD_TRAVELLER,
            MtaSelection.ADD_MEDICAL
          ])
        }
      })

      setSelectedPolicy({
        policyId,
        auth,
        actions,
        mta,
        callback: handleSetLoading,
        customerMTAOptions,
        track
      });
    }).catch(() => {
      setLoading(false);
    });
  }, [])

  const resetSelections = () => {
    setShowTravellerSelection(false);
    setShowTripDetailsSelection(false)
  }

  const goBackButton = (
    <Link to="/account/summary">
      <Button
        bsStyle={'primary'}
        block={true}
        className={'secondary-button'}>
        GO BACK
      </Button>
    </Link>
  )

  const goBackToSelectionButton = (
    <Button
      bsStyle={'primary'}
      block={true}
      className={'secondary-button'}
      onClick={resetSelections}
    >
      GO BACK
    </Button>
  )

  const canShowCancelPolicyOption = canCancel(customerMTAOptions);
  const renewal = findPolicyRenewal(auth, mta)
  const canShowUpsellOption = get(mta, 'rules.canUpsell', false);
  const canShowCancelRenewal = canCancel(customerMTAOptions, true)
  const insideCoolingOff = get(mta, 'policyInfo.meta.isInCoolingOff', false)

  if (mta.isLoading || loading) {
    return (
      <Loading/>
    )
  }

  if (!allowedMta && !loading) {
    return (
      <>
        <h1>Whoops!</h1>
        <p>Unfortunately, we have been unable to make the change you have requested.</p>
        <p>Please contact us in order to make the change and keep your policy up to date. Details on how to contact us
          can be found
          <a target={'_blank'} href={'https://www.admiral.com/contact-us/travel'}> here </a>
        </p>
        <p>It is important that your details are kept up to date, as incorrect information could invalidate your cover
          or affect any future claims.</p>
      </>
    )
  }

  const renderOptions = () => {
    const enabled = get(customerMTAOptions, 'additional_options.enabled', false);
    const allOptionsAdded = checkIfAllOptionsAreAdded(mta);
    const addedOptions = alreadyAddedOptions(mta)

    if (enabled && !allOptionsAdded) {
      return (
        <>
          <MTAAddOptions
            customerMTAOptions={customerMTAOptions}
            handleSelection={handleClick(policyId, actions, MtaSelection.ADDITIONAL_OPTION, null, 'cruise_cover')}
            text={'Add Cruise Cover'}
            addedOptions={addedOptions}
            optionKey={'cruise_cover'}
            icon={'boat.svg'}
            mta={mta}
          />
          <MTAAddOptions
            customerMTAOptions={customerMTAOptions}
            handleSelection={handleClick(policyId, actions, MtaSelection.ADDITIONAL_OPTION, null, 'winter_sports')}
            text={'Add Winter Sports Cover'}
            addedOptions={addedOptions}
            optionKey={'winter_sports'}
            icon={'ski.svg'}
            mta={mta}
          />
          <MTAAddOptions
            customerMTAOptions={customerMTAOptions}
            handleSelection={handleClick(policyId, actions, MtaSelection.ADDITIONAL_OPTION, null, 'gadget_cover')}
            text={'Add Gadget Cover'}
            addedOptions={addedOptions}
            optionKey={'gadget_cover'}
            icon={'headphone-symbol.svg'}
            mta={mta}
          />
          <MTAAddOptions
            customerMTAOptions={customerMTAOptions}
            handleSelection={handleClick(policyId, actions, MtaSelection.ADDITIONAL_OPTION, null, 'enhanced_trip_disruption')}
            text={'Add Enhanced Trip Disruption Cover'}
            addedOptions={addedOptions}
            optionKey={'enhanced_trip_disruption'}
            icon={'enhanced-trip-disruption.svg'}
            mta={mta}
          />
          <MTAAddOptions
            customerMTAOptions={customerMTAOptions}
            handleSelection={handleClick(policyId, actions, MtaSelection.ADDITIONAL_OPTION, null, 'excess_waiver')}
            text={'Add Excess Waiver'}
            addedOptions={addedOptions}
            optionKey={'excess_waiver'}
            icon={'no-money.svg'}
            mta={mta}
          />
        </>
      )
    }
    return null;

  }

  const renderTripDetailsSelection = () => {
    const schemeIsAnnual = get(mta, 'selectedPolicy.metadata.scheme_type', '') === 'annual';
    const showChangeTripDuration = get(mta, 'policyInfo.meta.upsellMaxTripDuration', 30) === 60;
    const coverLevel = get(actions.mta.getSelectedScheme(), 'scheme.cover_level', '').toLowerCase();
    const termNumber = get(mta, 'policyInfo.meta.termNumber', null);
    const canShowAMTChangeDate = new Date() < parse(mta.selectedPolicy.commencesAt, 'dd/MM/yyyy', new Date()) && termNumber === 1;
    const canShowAnnual = schemeIsAnnual && canShowAMTChangeDate;
    const isOptionEnabled = schemeIsAnnual ? customerMTAOptions?.change_start_date?.enabled : customerMTAOptions?.change_trip_date?.enabled;
    const canChangeTierWithCampaign = !get(mta, 'selectedPolicy.campaigns', []).some(camp => camp.metadata.prevent_mta_tier_change)

    return (
      <>

        <div style={{ marginTop: '15px' }}>

          <div className={'m-a-change-selection-container'}>

            <InfoAnnie title={'What would you like to update?'}/>

            <div className={'ma-a-change-selection-buttons-container'}>
              <MTAChangeDetails
                text={'Region'}
                icon={'continents3.svg'}
                largerIcon={true}
                handleClick={handleClick(policyId, actions, MtaSelection.CHANGE_REGION)}
                enabled={schemeIsAnnual && customerMTAOptions?.change_region?.enabled}
              />
              <MTAChangeDetails
                text={'Destination'}
                icon={'continents3.svg'}
                largerIcon={true}
                handleClick={handleClick(policyId, actions, MtaSelection.CHANGE_DESTINATION)}
                enabled={!schemeIsAnnual && customerMTAOptions?.change_destination?.enabled}
              />
              <MTAChangeDetails
                text={'Trip dates'}
                icon={'calendar2.svg'}
                handleClick={handleClick(policyId, actions, MtaSelection.CHANGE_TRIP_DATE)}
                enabled={(canShowAnnual && isOptionEnabled) || (!schemeIsAnnual && isOptionEnabled)}
              />
              <MTAChangeDetails
                text={'Maximum Trip Duration'}
                icon={'calendar2.svg'}
                handleClick={handleClick(policyId, actions, MtaSelection.CHANGE_TRIP_DURATION)}
                enabled={schemeIsAnnual && showChangeTripDuration &&
                  customerMTAOptions?.change_max_trip_duration?.enabled
                }
              />
              <MTAChangeDetails
                text={'Level of Cover'}
                icon={'legal-document.svg'}
                handleClick={handleClick(policyId, actions, MtaSelection.CHANGE_TIER)}
                enabled={insideCoolingOff && coverLevel !== 'platinum' && customerMTAOptions?.change_tier?.enabled && canChangeTierWithCampaign}
              />
            </div>

            <div className={'m-a-change-mta-button'}>
              <MTAButtons button1={goBackToSelectionButton}/>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderTravellerDetailsSelection = () => {
    const adults = get(mta, 'selectedPolicy.metadata.adults', []);
    const children = get(mta, 'selectedPolicy.metadata.children', []);
    const travellers = concat(adults, children);
    const cansShowRemoveTravellers = customerMTAOptions?.allow_remove_traveller?.enabled && insideCoolingOff && travellers.length > 1
    const disableAddTraveller = disabledMtaSelections.includes(MtaSelection.ADD_TRAVELLER)

    return (
      <>
        <div style={{ marginTop: '15px' }}>

          <div className={'m-a-change-selection-container'}>

            <InfoAnnie title={'What would you like to update?'}/>

            <div className={'ma-a-change-selection-buttons-container'}>
              <MTAChangeDetails
                text={'Add Traveller'}
                icon={'add-user.svg'}
                handleClick={handleClick(policyId, actions, MtaSelection.ADD_TRAVELLER, null, null, disableAddTraveller)}
                enabled={customerMTAOptions?.additional_travellers?.enabled }
              />
              <MTAChangeDetails
                text={'Update Medical Conditions'}
                icon={'aid-kit.svg'}
                handleClick={handleClick(policyId, actions, MtaSelection.ADD_MEDICAL, null, null, disableUpdateMedical)}
                enabled={customerMTAOptions?.change_medical?.enabled}
              />
              <MTAChangeDetails
                text={'Remove Traveller'}
                icon={'bin.svg'}
                handleClick={handleClick(policyId, actions, MtaSelection.REMOVE_TRAVELLER)}
                enabled={cansShowRemoveTravellers}
              />
            </div>

            <div className={'m-a-change-mta-button'}>
              <MTAButtons button1={goBackToSelectionButton}/>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (allowedMta && !loading) {

    if (showTravellerSelection) {
      return renderTravellerDetailsSelection()
    }

    if (showTripDetailsSelection) {
      return renderTripDetailsSelection()
    }

    return (
      <>

        <div style={{ marginTop: '15px' }}>

          <div className={'m-a-change-selection-container'}>

            <InfoAnnie title={'What would you like to update?'}/>

            <div className={'ma-a-change-selection-buttons-container'}>

              <MTAChangeDetails
                text={'Contact Info'}
                icon={'user.svg'}
                handleClick={handleClick(policyId, actions, MtaSelection.PERSONAL_DETAILS)}
              />
              <MTAChangeDetails
                text={'Policy & Trip Details'}
                icon={'insurance.svg'}
                handleClick={showTripDetailsOptions}
              />
              {showTravellerDetailsSection && (
                <MTAChangeDetails
                  text={'Traveller Details'}
                  icon={'patient.svg'}
                  handleClick={showTravellerOptions}
                />
              )}
              {showTravellerDetailsSection && (
                <MTAChangeDetails
                  text={'Update Medical Conditions'}
                  icon={'aid-kit.svg'}
                  handleClick={handleClick(policyId, actions, MtaSelection.ADD_MEDICAL, null, null, disableUpdateMedical)}
                  enabled={customerMTAOptions?.change_medical?.enabled}
                />
              )}
              {canShowUpsellOption && (
                <MTAChangeDetails
                  text={'Upgrade to Annual Multi Trip Cover'}
                  icon={'world.svg'}
                  handleClick={handleClick(policyId, actions, MtaSelection.AMT_UPSELL)}
                  enabled={customerMTAOptions?.change_trip_type?.enabled}
                />
              )}
              {renderOptions()}
              <MTARemoveOption
                mta={mta}
                customerMTAOptions={customerMTAOptions}
                handleSelection={handleClick(policyId, actions, MtaSelection.REMOVE_OPTION)}
              />
              {canShowCancelPolicyOption && (
                <MTAChangeDetails
                  text={'Cancel Active Policy'}
                  icon={'paper.svg'}
                  handleClick={handleClick(policyId, actions, MtaSelection.CANCEL_POLICY)}
                />
              )}
              {canShowCancelRenewal && renewal && (
                <MTAChangeDetails
                  text={'Cancel Renewal'}
                  icon={'calendar.svg'}
                  handleClick={handleClick(policyId, actions, MtaSelection.CANCEL_RENEWAL, renewal)}
                />
              )}
            </div>

            <div className={'m-a-change-mta-button'}>
              <MTAButtons button1={goBackButton}/>
            </div>
          </div>

        </div>
      </>
    );
  }
}

export default flow([
  withJob({
    work: (props: IMakeAChangeProps) => {
      const promises = [];

      promises.push(props.actions.branding.reLoadChannel(props.branding.defaultChannelKey));

      return Promise.all(promises);
    },
    LoadingComponent: () => <Loading/>,
    ErrorComponent: ({ error }) => {

      return (
        <div>
          {error.message}
        </div>
      );
    },
  }),
  connect(
    (state: IStore) => {
      return {
        auth: state.auth,
        mta: state.mta,
        branding: state.branding,
        customerMTAOptions: get(state.branding.channel.products[0], 'metadata.direct_customer_mta_options', null),
      };
    },
    (dispatch) => ({
      actions: {
        push: bindActionCreators(push, dispatch),
        reset: bindActionCreators(reset, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        destroy: bindActionCreators(destroy, dispatch),
        mta: bindActionCreators({ ...mtaActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
        paymentMethod: bindActionCreators({ ...paymentMethodActions }, dispatch),
      },
    }),
  ),
])(MakeAChangeSelection);
